import { useLocation } from "react-router-dom";

import { Logo } from "@/components/custom/logo";
import { APEX25_BRAND_PARAM } from "@/lib/apex25AppUrl";

const STORAGE_KEY = "auth-brand";

/** Remember for this tab that the visitor came through Apex 25 (e.g. from the demo results). */
export const rememberApex25Brand = () => {
  try {
    sessionStorage.setItem(STORAGE_KEY, APEX25_BRAND_PARAM);
  } catch {
    // Storage blocked: the brand simply isn't remembered.
  }
};

/** Whether this tab came through Apex 25, for screens outside the router's sign-in routes. */
export const isApex25BrandRemembered = (): boolean => {
  try {
    return sessionStorage.getItem(STORAGE_KEY) === APEX25_BRAND_PARAM;
  } catch {
    return false;
  }
};

/**
 * True when the visitor arrived from the Apex 25 marketing site. CTAs on tryapex25.com add
 * ?brand=apex25 to sign-in / sign-up links (see toApex25AppUrl). The answer is kept for the tab's
 * session because Clerk's path-routed steps (/sign-in/factor-one, ...) can drop the query string.
 *
 * The sign-in screen and the first onboarding step wear the Apex 25 mark for these visitors, with
 * "powered by theResume" under it, and stay theRecruit's for everyone else. The rest of the
 * product is theRecruit.
 */
export const useIsApex25Brand = (): boolean => {
  const { search } = useLocation();
  const fromQuery = new URLSearchParams(search).get("brand");
  try {
    if (fromQuery === APEX25_BRAND_PARAM) sessionStorage.setItem(STORAGE_KEY, fromQuery);
    return sessionStorage.getItem(STORAGE_KEY) === APEX25_BRAND_PARAM;
  } catch {
    return fromQuery === APEX25_BRAND_PARAM;
  }
};

/**
 * The one line that explains the change of name, shown under the Apex 25 mark. The mark itself
 * carries the brand, so this stays deliberately quiet — saying "Apex 25" twice on one screen is
 * what made the handover feel like a redirect to somewhere else.
 */
export const PoweredByTheResume = ({ className = "" }: { className?: string }) => (
  <p className={`text-xs text-[var(--technical-muted)] ${className}`}>powered by theResume</p>
);

/**
 * The Apex 25 lockup for visitors arriving from tryapex25.com, theRecruit's for everyone else.
 * `self-start` matters: the wide panel is a column flex container, which would otherwise stretch
 * the lockup to the full column width.
 */
export const BrandMark = ({ isApex25, height }: { isApex25: boolean; height: number }) =>
  isApex25 ? (
    <img
      src="/brand/apex25/logo.png"
      alt="Apex 25"
      height={height}
      width={Math.round((height * 672) / 160)}
      className="w-auto self-start object-contain"
      style={{ height }}
    />
  ) : (
    <Logo size={height} theme="light" />
  );
